import type { QueryClient } from "@tanstack/react-query";

import { agenticLogicKeys } from "@src/api/queryKeys";

import type { AgenticLogicWriteScope } from "./agenticLogicWriteEvents";

/**
 * Drops the cache entries a chat-side write moved.
 *
 * Invalidation, not seeding: unlike a cart run, none of these tool outputs is
 * the body a page query would have fetched — a publish answers a job id, a
 * lifecycle action answers the rule alone — so the refetch is the only way the
 * page sees the same thing the server does.
 *
 * `invalidateQueries` only refetches queries that are currently mounted, so
 * naming every rule-keyed entry costs nothing on a page that shows one of
 * them: a template rule's page has no draft query mounted, and the create page
 * has neither draft nor versions.
 */
export const invalidateAgenticLogicWrites = (
  queryClient: QueryClient,
  scopes: AgenticLogicWriteScope[]
) => {
  for (const scope of scopes) {
    for (const queryKey of keysForScope(scope)) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }
};

const keysForScope = (
  scope: AgenticLogicWriteScope
): (readonly unknown[])[] => {
  switch (scope.kind) {
    case "rule":
      return [
        agenticLogicKeys.rule(scope.ruleId),
        // Versions are a SEPARATE key from the rule, not a child of it: the
        // "not live yet" note diffs one against the other, so invalidating
        // only the rule leaves the note reading a stale half.
        agenticLogicKeys.versions(scope.ruleId),
        agenticLogicKeys.draft(scope.ruleId),
        // A save moves the source revision, and the example carts are
        // generated for a revision: without this the cart-test card keeps the
        // empty set it fetched before the rule had any source, and offers only
        // the catalog rows after the first save lands.
        agenticLogicKeys.scenarios(scope.ruleId),
        agenticLogicKeys.rules(),
      ];
    case "placements":
      return [agenticLogicKeys.placements()];
    case "assignments":
      return [agenticLogicKeys.assignments()];
    case "exclusions":
      return [agenticLogicKeys.exclusions()];
    case "dataset":
      return [
        // Every namespace filter, because a written dataset can land in any of
        // them — the same reason `allDatasets` exists.
        agenticLogicKeys.allDatasets(),
        ...(scope.key ? [agenticLogicKeys.dataset(scope.key)] : []),
      ];
  }
};
